import { useEffect, useState } from 'react';
import { Link, usePage } from '@inertiajs/react';

import Breadcrumbs from '@/components/public/ui/Breadcrumbs';
import Container from '@/components/public/ui/Container';
import Icon from '@/components/public/ui/Icon';
import Seo from '@/components/public/Seo';
import { PARISH_TIME_ZONE } from '@/lib/datetime';

type GalleryItem = {
  id: number;
  mediaType: 'image' | 'video';
  caption: string | null;
  altText: string | null;
  cover: boolean;
  mediaUrl: string | null;
};

type Gallery = {
  id: number;
  title: string;
  slug: string;
  description: string | null;
  publishedAt: string | null;
  eventTitle: string;
  eventStartsAt: string | null;
  eventLocation: string | null;
  itemCount: number;
  coverUrl: string | null;
  items: GalleryItem[];
};

type Props = {
  gallery: Gallery;
  [key: string]: unknown;
};

const dateFormatter = new Intl.DateTimeFormat('pt-PT', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: PARISH_TIME_ZONE,
});

function formatDate(isoString: string | null): string | null {
  if (!isoString) return null;
  return dateFormatter.format(new Date(isoString));
}

export default function GalleriesShow() {
  const { gallery } = usePage<Props>().props;
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const eventDate = formatDate(gallery.eventStartsAt);
  const activeItem = activeIndex === null ? null : gallery.items[activeIndex];
  const hasManyItems = gallery.items.length > 1;

  function closeLightbox() {
    setActiveIndex(null);
  }

  function showPrevious() {
    setActiveIndex((current) => {
      if (current === null) return current;
      return current === 0 ? gallery.items.length - 1 : current - 1;
    });
  }

  function showNext() {
    setActiveIndex((current) => {
      if (current === null) return current;
      return current === gallery.items.length - 1 ? 0 : current + 1;
    });
  }

  useEffect(() => {
    if (activeIndex === null) return;

    function handleKeyDown(ev: KeyboardEvent) {
      if (ev.key === 'Escape') closeLightbox();
      if (ev.key === 'ArrowLeft') {
        setActiveIndex((current) => {
          if (current === null) return current;
          return current === 0 ? gallery.items.length - 1 : current - 1;
        });
      }
      if (ev.key === 'ArrowRight') {
        setActiveIndex((current) => {
          if (current === null) return current;
          return current === gallery.items.length - 1 ? 0 : current + 1;
        });
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, gallery.items.length]);

  return (
    <>
      <Seo title={gallery.title} description={gallery.description ?? undefined} />
      <Container as="article" className="pt-12 pb-24 md:pt-16">
        <Breadcrumbs
          items={[{ label: 'Galerias', href: '/galleries' }, { label: gallery.title }]}
        />

        <header className="max-w-3xl">
          <span className="text-secondary inline-flex items-center gap-2.5 text-[11px] font-bold tracking-[0.14em] uppercase">
            <span className="bg-secondary inline-block h-0.5 w-5 rounded-full" />
            {gallery.eventTitle}
          </span>
          <h1 className="font-headline text-on-surface mt-3.5 text-4xl leading-[1.08] font-semibold md:text-5xl">
            {gallery.title}
          </h1>
          <div className="text-on-surface-light mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            {eventDate && <span>{eventDate}</span>}
            {gallery.eventLocation && <span>{gallery.eventLocation}</span>}
            <span>{gallery.itemCount} itens</span>
          </div>
          {gallery.description && (
            <p className="font-headline text-on-surface-light mt-5 text-lg leading-relaxed font-normal italic md:text-xl">
              {gallery.description}
            </p>
          )}
        </header>

        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {gallery.items.map((item, index) => (
            <figure key={item.id} className="group">
              <button
                type="button"
                onClick={() => setActiveIndex(index)}
                className="border-line bg-background aspect-[4/3] w-full overflow-hidden rounded-xl border text-left"
              >
                {item.mediaType === 'image' && item.mediaUrl ? (
                  <img
                    src={item.mediaUrl}
                    alt={item.altText || item.caption || `${gallery.title} ${index + 1}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : item.mediaType === 'video' && item.mediaUrl ? (
                  <video src={item.mediaUrl} className="h-full w-full object-cover" muted />
                ) : null}
              </button>
              {item.caption && (
                <figcaption className="text-on-surface-light mt-2 text-sm">
                  {item.caption}
                </figcaption>
              )}
            </figure>
          ))}
        </div>

        <footer className="border-line mt-10 border-t pt-6">
          <Link
            href="/galleries"
            className="text-primary hover:text-primary-dark inline-flex items-center gap-2 text-sm font-semibold transition-colors"
          >
            <Icon name="arrow_back" size="sm" />
            Voltar às Galerias
          </Link>
        </footer>
      </Container>

      {activeItem && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#17130f]/95 px-3 py-3 sm:px-6 sm:py-5"
          role="dialog"
          aria-modal="true"
          aria-label={`Imagem ${activeIndex === null ? '' : activeIndex + 1} de ${gallery.items.length}`}
        >
          <button
            type="button"
            className="absolute inset-0"
            onClick={closeLightbox}
            aria-label="Fechar galeria"
          />

          <div className="relative flex max-h-[96vh] w-full max-w-6xl flex-col gap-3 sm:max-h-[94vh] sm:gap-4">
            <div className="relative z-10 flex items-center justify-between gap-2 text-white sm:gap-3">
              <div className="min-w-0">
                <p className="text-secondary text-[11px] font-bold tracking-[0.14em] uppercase">
                  {activeIndex === null ? 1 : activeIndex + 1} de {gallery.items.length}
                </p>
                <p className="font-headline mt-1 truncate text-lg font-semibold sm:text-xl">
                  {gallery.title}
                </p>
              </div>
              <button
                type="button"
                onClick={closeLightbox}
                className="hover:border-secondary hover:text-secondary inline-flex min-h-10 shrink-0 items-center gap-1.5 rounded-md border border-white/15 bg-white/10 px-2.5 py-2 text-sm font-semibold text-white transition-colors sm:min-h-11 sm:gap-2 sm:px-3"
              >
                <Icon name="close" size="sm" />
                Fechar
              </button>
            </div>

            <div className="relative flex min-h-0 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-black/20 shadow-2xl">
              {hasManyItems && (
                <button
                  type="button"
                  onClick={showPrevious}
                  className="hover:text-secondary absolute inset-y-0 left-0 z-10 flex w-12 items-center justify-center bg-gradient-to-r from-black/55 to-transparent text-white transition-colors sm:w-24"
                  aria-label="Imagem anterior"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md border border-white/15 bg-white/10 sm:h-11 sm:w-11">
                    <Icon name="chevron_left" />
                  </span>
                </button>
              )}

              {activeItem.mediaType === 'image' && activeItem.mediaUrl ? (
                <img
                  src={activeItem.mediaUrl}
                  alt={activeItem.altText || activeItem.caption || gallery.title}
                  className="max-h-[68vh] object-contain sm:max-h-[76vh]"
                />
              ) : activeItem.mediaType === 'video' && activeItem.mediaUrl ? (
                <video
                  src={activeItem.mediaUrl}
                  className="max-h-[68vh] sm:max-h-[76vh]"
                  controls
                  autoPlay
                >
                  <track kind="captions" />
                </video>
              ) : null}

              {hasManyItems && (
                <button
                  type="button"
                  onClick={showNext}
                  className="hover:text-secondary absolute inset-y-0 right-0 z-10 flex w-12 items-center justify-center bg-gradient-to-l from-black/55 to-transparent text-white transition-colors sm:w-24"
                  aria-label="Imagem seguinte"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md border border-white/15 bg-white/10 sm:h-11 sm:w-11">
                    <Icon name="chevron_right" />
                  </span>
                </button>
              )}
            </div>

            {(activeItem.caption || hasManyItems) && (
              <div className="relative z-10 flex flex-col gap-2 border-t border-white/10 pt-3 text-white sm:flex-row sm:items-start sm:justify-between">
                {activeItem.caption && (
                  <p className="text-sm leading-relaxed text-white/78">{activeItem.caption}</p>
                )}
                {hasManyItems && (
                  <p className="hidden shrink-0 text-xs font-semibold tracking-[0.12em] text-white/45 uppercase sm:block">
                    Use ← → para navegar
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
